import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { toastr } from 'react-redux-toastr'
import axios from 'axios'
import _ from 'lodash'
import classnames from 'classnames'
import './Files.css'
import './SimpleFiles.css'
import {ChevronDown, ChevronUp, Upload, PenLine, X, Tag, Tags, Filter} from 'lucide-react'
import { defaultT } from '../utils/i18n'
import { isValidFilename } from '../server/utils/isValidFilename'
import { TrashCell } from './DataTable'
import moment from "moment"
import FilePreviewAndDownload from "./filePreviewAndDownload";
import UserPicker from './Conversation/utils/userPicker'
import UploadHint from './UploadHint'
import { isTooLarge } from '../utils/uploadLimits'
//import { ProgressBar } from './ProgressBar/ProgressBar'

function formatFileDate(dateString, t) {
    if (!dateString) return '';

    // Split the date string into parts
    const parts = dateString.split(' ');

    if (parts.length === 2) {
        return `${parts[0]} ${t('at').toLowerCase()} ${parts[1]}`;
    }

    return dateString;
}

const ProgressBar = ({ percentage, color }) => (
    <div className="progress-bar-container">
        <div
            className={percentage === 100 ? 'progress-bar-fill upload-processing' : 'progress-bar-fill'}
            style={{ width: `${percentage}%`, backgroundColor: color }}
        />
    </div>
);

class SimpleFiles extends Component {
    state = {
        uploadState: {}, // Track per-file progress
        hovered: false,
        expanded: false,
        isDragging: false,
        editingFileId: null,
        editingValue: '',
        tagPickerFileId: null,
        customTagLabel: '',
        customTagColor: '#607d8b',
        customTagColorPicked: false,
        tagFilter: [],
        editingPresetIndex: null,
        editingPresetValue: '',
        presetTags: [
            { id: 'red', name: 'Red', color: '#e74c3c' },
            { id: 'amber', name: 'Amber', color: '#f39c12' },
            { id: 'blue', name: 'Blue', color: '#3498db' },
            { id: 'purple', name: 'Purple', color: '#9b59b6' },
            { id: 'green', name: 'Green', color: '#2ecc71' },
        ]
    }

    tagPickerRef = React.createRef()

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutsideClick)
    }

    handleOutsideClick = (e) => {
        if (this.tagPickerRef.current && !this.tagPickerRef.current.contains(e.target)) {
            this.closeTagPicker()
        }
    }

    openTagPicker = (fileId) => {
        this.setState({ tagPickerFileId: fileId, customTagLabel: '', customTagColor: '#607d8b', customTagColorPicked: false })
        document.addEventListener('mousedown', this.handleOutsideClick)
    }

    closeTagPicker = () => {
        this.setState({ tagPickerFileId: null, customTagLabel: '', customTagColor: '#607d8b', customTagColorPicked: false, editingPresetIndex: null, editingPresetValue: '' })
        document.removeEventListener('mousedown', this.handleOutsideClick)
    }


    applyTag = (file, tag) => {
        const index = this.findRowIndex(file.id)
        const currentTags = file.tags || (file.tag ? [file.tag] : [])
        const alreadyExists = currentTags.some(t => t.id === tag.id)
        if (alreadyExists) return
        this.props.remove(index)
        this.props.insert(index, { ...file, tags: [...currentTags, tag] })
    }

    removeTag = (file, tag) => {
        const index = this.findRowIndex(file.id)
        const currentTags = (file.tags || (file.tag ? [file.tag] : [])).filter(
            t => t.id !== tag.id
        )
        this.props.remove(index)
        this.props.insert(index, { ...file, tags: currentTags })
    }

    setPresetName = (index, name) => {
        this.setState(prevState => {
            const presetTags = prevState.presetTags.map((p, i) => i === index ? { ...p, name } : p)
            return { presetTags }
        })
    }

    handleResponse(data, t) {
        const { push, user, anonymous } = this.props
        if (data) {
            const userObject = !!anonymous ? {} : {user}
            push({
                ...data,
                ...userObject,
                date: moment().format('YYYY-MM-DD HH:mm')
            })
        }
        else {
            toastr.error(t('errorUploading'))
        }
    }

    async handleOneFileUpload(file, t, allFiles){
        const { allowedFormats } = this.props
        if (allowedFormats && allowedFormats.length > 0) {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
            if (!allowedFormats.some(format => format.toLowerCase() === fileExtension)) {
                toastr.error(t('unsupportedFileType', {
                    allowedFormats: allowedFormats.join(', ')
                }))
                return
            }
        }

        if (await isTooLarge(file, 'file')) {
            toastr.error(t('tooLargeFile'))
            return
        }

        let data = new FormData()
        const filenameNotUnique = allFiles.some(obj => obj.filename === file.name)
        const fileName = filenameNotUnique ? `${moment().format('YYYY-MM-DD_HH-mm-ss')}_${file.name}` : file.name
        const fileType = file.type
        const fileSize = file.size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        let fileChunk = null, chunkNb = 0

        for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
            if (start + chunkSize > fileSize) { fileChunk = file.slice(start, fileSize) }
            else { fileChunk = file.slice(start, start + chunkSize) }
            data.append('fileChunks', fileChunk, fileName)
        }
        data.append('fileType', fileType)
        data.append('fileSize', fileSize)
        data.append('chunkNb', chunkNb)

        const options = {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent
                let percent = Math.floor( (loaded * 100) / total )
                this.setState(prevState => ({
                    uploadState: {
                        ...prevState.uploadState,
                        [file.name]: percent
                    }
                }))
            }
        }

        try {
            const res = await axios.post(`/${this.props.url}`, data, options)
            this.handleResponse(res.data, t)
            toastr.success(t('successFileUploading'))
        } catch (err) {
            const data = _.get(err, 'response.data', {})
            toastr.error(t(data.message || 'errorUploading', data))
        } finally {
            this.setState(prevState => {
                const progress = { ...prevState.uploadState }
                delete progress[file.name]
                return { uploadState: progress }
            })
        }
    }

    handleFilesUpload = (t, allFiles) => async (event) => {
        const files = Array.from(event.target.files) // copy the FileList early
        event.target.value = null // reset to null to allow re-uploading the same files

        for (let i = 0; i < files.length; i++) {
            await this.handleOneFileUpload(files[i], t, allFiles)
        }
    }

    findRowIndex = (rowId) => {
        const { allIds } = this.props

        return allIds.findIndex(id => id === rowId)
    }

    startEditingFilename = (file) => {
        const dotIndex = file.filename.lastIndexOf('.')
        const baseName = dotIndex !== -1 ? file.filename.slice(0, dotIndex) : file.filename
        this.setState({ editingFileId: file.id, editingValue: baseName })
    }

    commitFilenameEdit = (file) => {
        const { editingValue } = this.state
        const { insert, t = defaultT } = this.props
        const dotIndex = file.filename.lastIndexOf('.')
        const ext = dotIndex !== -1 ? file.filename.slice(dotIndex) : ''
        const newName = (editingValue.trim() || file.filename.slice(0, dotIndex || undefined)) + ext

        if (newName !== file.filename) {
            if (!isValidFilename(newName)) {
                toastr.error(t('invalidFileName'))
                this.setState({ editingFileId: null, editingValue: '' })
                return
            }
            const index = this.findRowIndex(file.id)
            this.props.remove(index)
            insert(index, { ...file, filename: newName })
        }

        this.setState({ editingFileId: null, editingValue: '' })
    }

    cancelFilenameEdit = () => {
        this.setState({ editingFileId: null, editingValue: '' })
    }

    renderDeleteIcon = (file) => {
        return <TrashCell onClick={() => {
            const index = this.findRowIndex(file.id)
            this.props.remove(index)
        }} />
    }

    renderDownloadAndPreview = (file, language, groupModel, accessId, files) => {
        return <FilePreviewAndDownload
            key={file.id}
            file={file}
            language = {language}
            groupModel={groupModel}
            accessId={accessId}
            files={files}
            url={this.props.url}
        />
    }

    renderTagPicker = (file) => {
        const { presetTags, customTagLabel, customTagColor, customTagColorPicked } = this.state
        const { allIds = [], byId = {} } = this.props

        const usedTags = _.uniqBy(
            allIds.flatMap(id => byId[id]?.tags || (byId[id]?.tag ? [byId[id].tag] : [])),
            tag => tag.id
        )

        return (
            <div className="file-tag-picker" ref={this.tagPickerRef}>
                <div className="tag-custom-row">
                    <div className="tag-preset-swatch tag-custom-swatch" style={customTagColorPicked ? { background: customTagColor } : {}}>
                        <input
                            type="color"
                            className="tag-custom-color-input"
                            value={customTagColor}
                            onChange={e => this.setState({ customTagColor: e.target.value, customTagColorPicked: true })}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                    <input
                        className="tag-custom-label-input"
                        placeholder="+ Add a custom label..."
                        value={customTagLabel}
                        onChange={e => this.setState({ customTagLabel: e.target.value })}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                const name = customTagLabel.trim()
                                if (name) this.applyTag(file, { id: name.toLowerCase(), name, color: customTagColor })
                            }
                            if (e.key === 'Escape') this.closeTagPicker()
                        }}
                    />
                </div>

                {usedTags.length > 0 && (
                    <>
                        <div className="tag-section-label">Used</div>
                        {usedTags.map((tag, i) => {
                            const fileTags = file.tags || (file.tag ? [file.tag] : [])
                            const isSelected = fileTags.some(t => t.id === tag.id)
                            const onlyInCurrentFile = false //allIds.filter(id => byId[id]?.tag?.id === tag.id).length === 1 && isSelected
                            return (
                                <div
                                    key={i}
                                    className={`tag-preset-row${isSelected ? ' tag-row-selected' : ''}`}
                                    onClick={() => isSelected ? this.removeTag(file, tag) : this.applyTag(file, tag)}
                                >
                                    <div className="tag-preset-swatch" style={{ backgroundColor: tag.color }} />
                                    {onlyInCurrentFile ? (
                                        <input
                                            className="tag-preset-label-input"
                                            defaultValue={tag.name}
                                            onClick={e => e.stopPropagation()}
                                            onBlur={e => {
                                                const newName = e.target.value.trim()
                                                if (newName && newName !== tag.name) this.applyTag(file, { ...tag, name: newName })
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') e.currentTarget.blur()
                                                if (e.key === 'Escape') { e.currentTarget.value = tag.name; e.currentTarget.blur() }
                                            }}
                                        />
                                    ) : (
                                        <span className="tag-used-label">{tag.name}</span>
                                    )}
                                </div>
                            )
                        })}
                    </>
                )}

                <>
                    <div className="tag-section-label">Presets</div>
                    {presetTags.filter(p => !usedTags.some(u => u.id === p.id)).map((preset) => {
                        const realIndex = presetTags.findIndex(p => p.id === preset.id)
                        return (
                            <div
                                key={preset.id}
                                className="tag-preset-row"
                                onClick={() => this.applyTag(file, { id: preset.id, name: preset.name, color: preset.color })}
                            >
                                <div
                                    className="tag-preset-swatch"
                                    style={{ backgroundColor: preset.color }}
                                />
                                <input
                                    className="tag-preset-label-input"
                                    value={preset.name}
                                    onChange={e => this.setPresetName(realIndex, e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur()
                                    }}
                                />
                            </div>
                        )
                    })}
                </>
            </div>
        )
    }


    render() {
        const { allIds = [], byId = {}, t = defaultT, placeholder, allowedFormats, editable, style, language, groupModel, accessId } = this.props
        const accept = Array.isArray(allowedFormats) && allowedFormats.length > 0
            ? allowedFormats.join(',')
            : undefined // accept all types
        const hasFiles = allIds.length > 0;
        const hasAtLeastTwoFiles = allIds.length > 1;
        const hasUploading = Object.keys(this.state.uploadState).length > 0;

        const { tagFilter } = this.state

        const usedTags = _.uniqBy(
            allIds.flatMap(id => byId[id]?.tags || (byId[id]?.tag ? [byId[id].tag] : [])),
            tag => tag.id
        )
        const hasFilesWithNoTags = allIds.some(id => {
            const ft = byId[id]?.tags || (byId[id]?.tag ? [byId[id].tag] : [])
            return ft.length === 0
        })
        const tagItems = [
            ...(hasFilesWithNoTags ? [{ id: '__no_tags__', name: 'No tags', color: '#ccc' }] : []),
            ...usedTags
        ]

        const tagFilteredIds = tagFilter.length === 0 ? allIds : allIds.filter(id => {
            const ft = byId[id]?.tags || (byId[id]?.tag ? [byId[id].tag] : [])
            return tagFilter.some(key => {
                if (key === '__no_tags__') return ft.length === 0
                return ft.some(t => t.id === key)
            })
        })
        const filteredFilesIds = this.state.expanded ? tagFilteredIds : tagFilteredIds.slice(-1)
        const files = allIds.map(id => byId[id])

        return (
            <div className="upload-section" style={style}>
                <div className="file-list-wrapper">
                    {!hasFiles && !hasUploading ? (
                            // Empty State
                            <div
                                className={`empty-state${this.state.isDragging ? ' drag-over' : ''}`}
                                style={style}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: true }); }}
                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) this.setState({ isDragging: false }); }}
                                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: false }); this.handleFilesUpload(t, files)({ target: { files: e.dataTransfer.files } }); }}
                            >
                                Aucun fichier n'a encore été ajouté
                                {editable && (
                                    <div className="buttons-container">
                                        <>
                                            <div style={{ position: 'relative', width: 28, height: 28 }}>
                                                <button
                                                    type="button"
                                                    disabled={!editable}
                                                    className={
                                                        classnames(
                                                            "add-file-btn",
                                                            {hovered: this.state.hovered}
                                                        )
                                                    }
                                                >
                                                    <Upload size={16}></Upload>
                                                </button>
                                                <input
                                                    disabled={!editable}
                                                    className="file-upload-input"
                                                    type="file"
                                                    accept={accept}
                                                    multiple
                                                    encType='multipart/form-data'
                                                    onMouseEnter={() => this.setState({hovered: true})}
                                                    onMouseLeave={() => this.setState({hovered: false})}
                                                    onDragOver={() => this.setState({hovered: true})}
                                                    onChange={this.handleFilesUpload(t, files)}
                                                />
                                            </div>
                                        </>
                                    </div>
                                )}
                            </div>
                        ) : (
                        <div
                            className={`file-list-container${this.state.isDragging ? ' drag-over' : ''}`}
                            style={style}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: true }); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) this.setState({ isDragging: false }); }}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: false }); this.handleFilesUpload(t, files)({ target: { files: e.dataTransfer.files } }); }}
                        >
                            {/* File count badge - positioned absolutely relative to file-list-container */}
                            {!this.state.expanded && hasAtLeastTwoFiles && <div className="file-count-badge">
                                {allIds.length}
                            </div>}

                            <div className="file-list">
                                {!this.state.expanded && hasAtLeastTwoFiles && (() => {
                                    const visibleFileTags = byId[filteredFilesIds[0]]?.tags || (byId[filteredFilesIds[0]]?.tag ? [byId[filteredFilesIds[0]].tag] : [])
                                    const visibleTag = visibleFileTags[0]
                                    const tagProps = visibleTag ? {
                                        className: 'file-card extra-card has-tag',
                                        style: {
                                            '--tag-color': visibleTag.color,
                                            '--tag-bg': visibleTag.color + '1a',
                                            '--tag-border-color': visibleTag.color + '5a'
                                        }
                                    } : { className: 'file-card extra-card' }
                                    return <>
                                        <div {...tagProps}></div>
                                        <div {...tagProps}></div>
                                    </>
                                })()}
                                {filteredFilesIds.map(id => {
                                    const file = byId[id];
                                    const fileTags = file.tags || (file.tag ? [file.tag] : [])
                                    const firstTag = fileTags[0]
                                    return (
                                        <div key={id} className={`file-card main-card${this.state.tagPickerFileId === id ? ' tag-picker-open' : ''}${fileTags.length > 0 ? ' has-tag' : ''}`} style={firstTag ? { '--tag-color': firstTag.color, '--tag-bg': firstTag.color + '1a', '--tag-bg-hover': firstTag.color + '1a', '--tag-border-color': firstTag.color + '5a'} : {}}>
                                            <div className="file-info">
                                                {editable && this.state.editingFileId === file.id ? (
                                                    <div className="file-fileName-edit-row">
                                                        <div className="file-fileName-input-sizer" data-value={this.state.editingValue}>
                                                            <input
                                                                className="file-fileName-input"
                                                                autoFocus
                                                                size={1}
                                                                value={this.state.editingValue}
                                                                onChange={e => this.setState({ editingValue: e.target.value })}
                                                                onBlur={() => this.commitFilenameEdit(file)}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') this.commitFilenameEdit(file)
                                                                    if (e.key === 'Escape') this.cancelFilenameEdit()
                                                                }}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        {file.filename.lastIndexOf('.') !== -1 && (
                                                            <span className="file-fileName-ext">
                                                                {file.filename.slice(file.filename.lastIndexOf('.'))}
                                                            </span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="file-fileName-edit-btn"
                                                            style={{ marginLeft: `-3px` }}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                this.cancelFilenameEdit();
                                                            }}
                                                        >
                                                            <X size={12} color="#dd3c1b" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="file-fileName">
                                                        {file.filename}
                                                        {editable && file.user?.id === this.props.user?.id && (
                                                            <button
                                                                type="button"
                                                                className="file-fileName-edit-btn"
                                                                onClick={() => this.startEditingFilename(file)}
                                                            >
                                                                <PenLine size={12} color="#3498db" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="file-meta">{formatFileDate(file.date, t)} {file.user?.name ? ` • ${file.user.name}` : ''}</div>

                                                {/* Tag area */}
                                                <div className="file-tag-area">
                                                    {fileTags.map((tag, i) => (
                                                        <span
                                                            key={i}
                                                            className="file-tag-badge"
                                                            style={{ backgroundColor: tag.color }}
                                                            onMouseDown={editable ? e => { e.stopPropagation(); this.openTagPicker(file.id) } : undefined}
                                                        >
                                                            <Tag size={12} style={{ flexShrink: 0, strokeWidth: 2 }} />
                                                            {tag.name}
                                                        </span>
                                                    ))}
                                                    {editable && !fileTags.length && (
                                                        <span className="file-tag-ghost" onMouseDown={e => { e.stopPropagation(); this.openTagPicker(file.id) }}>
                                                            <Tag size={12} color="#3498db" style={{ flexShrink: 0, strokeWidth: 2 }}/>
                                                        </span>
                                                    )}

                                                    {editable && this.state.tagPickerFileId === file.id && this.renderTagPicker(file)}
                                                </div>
                                            </div>
                                            <div className="file-actions">
                                                {this.renderDownloadAndPreview(file, language, groupModel, accessId, files)}
                                                {editable && this.renderDeleteIcon(file)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="buttons-container">
                                {hasFiles && this.state.expanded && tagItems.length > 0 && (
                                    <UserPicker
                                        users={tagItems}
                                        preselectedUsers={tagItems.filter(t => tagFilter.includes(t.id))}
                                        onChange={(selected) => this.setState({ tagFilter: selected.map(t => t.id) })}
                                        triggerIcon={<Tags size={14} />}
                                        triggerStyle={{ minHeight: '28px', height: '32px', borderRadius: '18px', background: '#F8FAFC' }}
                                        renderItem={(tag) => (
                                            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: tag.color, flexShrink: 0 }} />
                                        )}
                                        menuWidth='220px'
                                    />
                                )}
                                {editable && <>
                                    <div style={{ position: 'relative', width: 28, height: 28 }}>
                                        <button
                                            type="button"
                                            disabled={!editable}
                                            className={
                                                classnames(
                                                    "add-file-btn",
                                                    {hovered: this.state.hovered}
                                                )
                                            }
                                        >
                                            <Upload size={16}></Upload>
                                        </button>
                                        <input
                                            type="file"
                                            accept={accept}
                                            multiple
                                            disabled={!editable}
                                            className="file-upload-input"
                                            encType='multipart/form-data'
                                            onMouseEnter={() => this.setState({hovered: true})}
                                            onMouseLeave={() => this.setState({hovered: false})}
                                            onDragOver={() => this.setState({hovered: true})}
                                            onChange={this.handleFilesUpload(t, files)}
                                        />
                                    </div>
                                </>}
                                {hasAtLeastTwoFiles && <button
                                    type="button"
                                    className="expand-btn"
                                    onClick={() => this.setState(prevState => ({ expanded: !prevState.expanded }))}
                                >
                                    {
                                        this.state.expanded ?
                                        <ChevronUp size={17}></ChevronUp> : <ChevronDown size={17}></ChevronDown>
                                    }
                                </button>}
                            </div>
                        </div>
                    )}
                </div>
                {editable && <UploadHint kind="file" formats={allowedFormats} t={t} />}

                {/* Upload Progress */
                //other color //#2ecc71 //#E94E24
                }
                {hasUploading && (
                    <div className="upload-progress-container">
                        {Object.entries(this.state.uploadState).map(([filename, percent]) => (
                            <div key={filename} className="upload-progress-item">
                                <div className="upload-progress-info">
                                    <span className="upload-filename">{filename}</span>
                                    <span className="upload-percentage">{percent === 100 ? t('processingFile') : `${percent}%`}</span>
                                </div>
                                <ProgressBar percentage={percent} color='#2ecc71'/>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }
}

SimpleFiles.propTypes = {
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    allowedFormats: PropTypes.arrayOf(PropTypes.string),
    t: PropTypes.func
}

export default SimpleFiles